import { useCallback, useEffect, useRef, useState } from 'react';
import TopolPlugin from '@topol.io/editor';

import { getTopolOptions, LOAD_TIMEOUT, MAX_RETRIES, RETRY_DELAY } from '../topolConfig';

export const SAVE_MODES = {
    SAVE: 'save',
    PREVIEW: 'preview',
};

export const STATUS = {
    INITIALIZING: 'initializing',
    READY: 'ready',
    ERROR: 'error',
};

export const useTopolEditor = ({
    containerRef,
    containerId,
    config,
    theme,
    onSave,
    onInit,
    onPreview,
    onError,
    isOpen,
    variables,
}) => {
    const topolInitialized = useRef(false);
    const loadTimeoutRef = useRef(null);
    const retryTimeoutRef = useRef(null);
    const currentConfigRef = useRef(config);
    const saveModeRef = useRef({ mode: SAVE_MODES.SAVE });
    const onSaveRef = useRef(onSave);
    const onInitRef = useRef(onInit);
    const onErrorRef = useRef(onError);
    const onPreviewRef = useRef(onPreview);
    useEffect(() => {
        onSaveRef.current = onSave;
        onInitRef.current = onInit;
        onErrorRef.current = onError;
        onPreviewRef.current = onPreview;
    }, [onSave, onInit, onError, onPreview]);

    useEffect(() => {
        currentConfigRef.current = config;
    }, [config]);

    const [status, setStatus] = useState(STATUS.INITIALIZING);
    const [errorMessage, setErrorMessage] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    const clearTimers = useCallback(() => {
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
        }

        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, []);

    const destroy = useCallback(() => {
        if (!topolInitialized.current) return;
        try {
            TopolPlugin?.destroy?.();
        } catch (e) {
            // Ignore destroy errors
        }
        // Clear container content to ensure clean state
        if (containerRef.current) {
            containerRef.current.innerHTML = '';
        }
    }, [containerRef]);

    const initialize = useCallback(
        (retryAttempt = 0) => {
            const hasContainer = !!containerRef.current;
            const hasTopolPlugin = TopolPlugin && typeof TopolPlugin.init === 'function';

            if (!hasContainer) {
                setStatus(STATUS.ERROR);
                setErrorMessage('Container not available');
                setRetryCount(retryAttempt);
                return;
            }

            if (!hasTopolPlugin) {
                if (retryAttempt < MAX_RETRIES) {
                    setStatus(STATUS.INITIALIZING);
                    setErrorMessage(null);
                    setRetryCount(retryAttempt + 1);

                    retryTimeoutRef.current = setTimeout(
                        () => initialize(retryAttempt + 1),
                        RETRY_DELAY,
                    );
                } else {
                    setStatus(STATUS.ERROR);
                    setErrorMessage('Topol plugin not available after retries');
                    setRetryCount(retryAttempt);
                }

                return;
            }

            const options = getTopolOptions({
                containerId,
                onSave: (json, html) => {
                    const mode = saveModeRef.current.mode;

                    const saveMethods = {
                        [SAVE_MODES.SAVE]: () => onSaveRef.current?.({ html, topolJson: json }),
                        [SAVE_MODES.PREVIEW]: () => onPreviewRef.current?.({ html }),
                    };

                    saveMethods[mode]?.();
                    saveModeRef.current.mode = SAVE_MODES.SAVE;
                },
                onInit: () => {
                    if (loadTimeoutRef.current) {
                        clearTimeout(loadTimeoutRef.current);
                        loadTimeoutRef.current = null;
                    }

                    setStatus(STATUS.READY);
                    setErrorMessage(null);
                    setRetryCount(0);
                    onInitRef.current?.();
                },
                onError: (error) => {
                    setStatus(STATUS.ERROR);
                    setErrorMessage(error?.message || 'Unknown error initializing editor');
                    setRetryCount(0);
                    onErrorRef.current?.(error);
                },
                theme,
                variables,
            });

            try {
                TopolPlugin.init(options);
                topolInitialized.current = true;

                loadTimeoutRef.current = setTimeout(() => {
                    setStatus((prevStatus) => {
                        if (prevStatus !== STATUS.READY) {
                            return STATUS.ERROR;
                        }
                        return prevStatus;
                    });
                    setErrorMessage((prevError) => {
                        if (status !== STATUS.READY) {
                            return 'Editor loading timeout - please refresh the page';
                        }
                        return prevError;
                    });
                }, LOAD_TIMEOUT);
            } catch (error) {
                setStatus(STATUS.ERROR);
                setErrorMessage(error?.message || 'Failed to initialize editor');
                setRetryCount(0);
            }
        },
        [containerRef, containerId, theme, status, variables],
    );

    const retry = useCallback(() => {
        clearTimers();
        destroy();
        topolInitialized.current = false;
        setStatus(STATUS.INITIALIZING);
        setErrorMessage(null);
        setRetryCount(0);
        initialize();
    }, [clearTimers, destroy, initialize]);

    const save = useCallback((mode = SAVE_MODES.SAVE) => {
        saveModeRef.current.mode = mode;
        TopolPlugin?.save?.();
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        if (topolInitialized.current) return;

        initialize();

        return () => {
            clearTimers();
            destroy();
            topolInitialized.current = false;
        };
    }, [clearTimers, destroy, initialize, isOpen]);

    useEffect(() => {
        const configValue = currentConfigRef.current;
        const isReady = status === STATUS.READY;

        if (!isOpen || !isReady || !configValue) return;

        try {
            TopolPlugin?.load?.(configValue);
        } catch (error) {
            console.error('Error parsing Topol JSON, falling back to HTML:', error);
        }
    }, [status, isOpen]);

    return {
        status,
        errorMessage,
        retryCount,
        initialize,
        destroy,
        retry,
        save,
    };
};
